import React from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAccount, useDisconnect } from 'wagmi';
import { ReactComponent as CloseButtonDark } from '../assets/svg/modal_close_btn-dark.svg';
import { ReactComponent as CloseButtonLight } from '../assets/svg/modal_close_btn-light.svg';
import { ReactComponent as WalletIcon } from '../assets/svg/wallet.svg';
import modalTextureDark from '../assets/png/modal_texture-dark.png';
import modalTextureLight from '../assets/png/modal_texture-light.png';

interface ConnectedWalletModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ConnectedWalletModal: React.FC<ConnectedWalletModalProps> = ({ isOpen, onClose }) => {
  const { theme } = useTheme();
  const { address } = useAccount();
  const { disconnect } = useDisconnect();

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleDisconnect = (): void => {
    disconnect();
    onClose();
  };

  const formatFullAddress = (address: string | undefined): string => {
    if (!address) return '';
    // Show more of the address in the modal
    return `${address.slice(0, 26)}...${address.slice(-4)}`;
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-85 flex items-end md:items-center justify-center z-[9999]"
      onClick={handleBackdropClick}
    >
      <div 
        className="relative w-full md:w-[400px] h-auto md:h-[362px] bg-contrast-8 dark:bg-contrast-2 rounded-t-[16px] md:rounded-[16px] overflow-hidden border border-contrast-7 dark:border-transparent max-w-full md:max-w-none"
        style={{
          backgroundImage: theme === 'dark' ? `url(${modalTextureDark})` : `url(${modalTextureLight})`,
          backgroundSize: theme === 'dark' ? '100% auto' : 'auto 120px',
          backgroundPosition: theme === 'dark' ? 'bottom' : 'bottom right',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Content Wrapper */}
        <div className="p-6 h-full flex flex-col relative z-10">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-contrast-3 dark:text-contrast-7 text-[18px] font-semibold font-geist">
              Connected Wallet
            </h2>
            <button 
              onClick={onClose}
              className="w-[40px] h-[40px] flex items-center justify-center"
            >
              {theme === 'dark' ? (
                <CloseButtonDark className="w-[40px] h-[40px]" />
              ) : (
                <CloseButtonLight className="w-[40px] h-[40px]" />
              )}
            </button>
          </div>

          {/* Wallet Address Section */}
          <div className="bg-white dark:bg-contrast-3 border border-contrast-7 dark:border-transparent rounded-lg px-4 h-[72px] flex items-center mb-auto">
            {/* Wallet Icon */}
            <div className="w-6 h-6 mr-3 flex items-center justify-center">
              <WalletIcon className="w-6 h-6" />
            </div>
            
            {/* Address */}
            <button 
              onClick={() => window.open(`https://basescan.org/address/${address}`, '_blank')}
              className="text-contrast-3 dark:text-contrast-7 text-[14px] font-medium font-geist-mono underline hover:text-primary-azul transition-colors cursor-pointer"
            >
              {formatFullAddress(address)}
            </button>
          </div>

          {/* Disconnect Button */}
          <button 
            onClick={handleDisconnect}
            className="w-full h-12 bg-primary-azul hover:bg-opacity-90 hover:shadow-none text-white font-medium font-inter text-[14px] rounded-[8px] transition-all shadow-[0_2px_12px_0_rgba(123,138,255,0.10),0_0_14px_0_rgba(118,133,255,0.50)_inset,0_0_16px_0_rgba(90,63,229,0.15)] mt-8 flex items-center justify-center"
          >
            Disconnect Wallet
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConnectedWalletModal;